import React from 'react';
import { MapPin, Star } from 'lucide-react';
import { Button } from './ui/button';

const HeroLuxury = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Single Static Background Image with Subtle Zoom */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: 'url(https://customer-assets-eiarnc6j.emergentagent.net/job_41b15d87-444c-4c23-b5eb-cc5f053f8771/artifacts/7375abff1c7a9f89_Golden%20Sunset%20Over%20Cape%20Town%20Bay.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          animation: 'subtleZoom 20s ease-in-out infinite alternate'
        }}
      >
        {/* Preload image for instant display */}
        <link rel="preload" as="image" href="https://customer-assets-eiarnc6j.emergentagent.net/job_41b15d87-444c-4c23-b5eb-cc5f053f8771/artifacts/7375abff1c7a9f89_Golden%20Sunset%20Over%20Cape%20Town%20Bay.png" />
      </div>
      
      {/* Gradient Overlays - Smooth blend for text readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-sky-500/20 via-sky-800/40 to-black/80"></div>
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/60"></div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#6B0B0B]/20 via-transparent to-[#D4AF37]/15 opacity-60"></div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 py-20 text-center">
        {/* Location Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-6 animate-fade-in">
          <MapPin size={16} className="text-[#D4AF37]" />
          <span className="text-sm font-medium text-white tracking-wide">Cape Town, South Africa</span>
        </div>

        {/* Main Heading with Cinematic Typography */}
        <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-bold text-white mb-6 tracking-tight leading-[1.1] animate-slide-up drop-shadow-2xl">
          Discover the Magic of
          <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] via-[#F7EFE5] to-[#C5A059] drop-shadow-lg">
            Cape Town
          </span>
        </h1>

        {/* Subtitle */}
        <p className="text-lg md:text-xl text-gray-100 max-w-3xl mx-auto mb-4 leading-relaxed animate-slide-up animation-delay-200 drop-shadow-lg">
          Experience unforgettable adventures, breathtaking landscapes, and world-class hospitality 
          with Cape Atlantic Tours
        </p>

        {/* Rating */}
        <div className="flex items-center justify-center gap-2 mb-8 animate-slide-up animation-delay-300">
          {[...Array(5)].map((_, i) => (
            <Star key={i} size={20} fill="#D4AF37" className="text-[#D4AF37] drop-shadow-md" />
          ))}
          <span className="text-white/90 ml-2 font-medium drop-shadow-md">5.0 Luxury Experience</span>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-slide-up animation-delay-400">
          <Button 
            onClick={() => scrollToSection('tours')}
            className="px-8 py-6 text-lg font-semibold bg-[#6B0B0B] hover:bg-[#8B1515] text-white rounded-full shadow-2xl hover:shadow-[#6B0B0B]/50 transition-all duration-300 transform hover:scale-105 gold-border"
          >
            Explore Our Tours
            <span className="ml-2">→</span>
          </Button>
          <Button 
            onClick={() => scrollToSection('contact')}
            variant="outline"
            className="px-8 py-6 text-lg font-semibold bg-white/10 backdrop-blur-md border-2 border-[#D4AF37] text-white hover:bg-[#D4AF37] hover:text-[#0B090A] rounded-full transition-all duration-300 transform hover:scale-105"
          >
            Book Your Journey
          </Button>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 rounded-full border-2 border-[#D4AF37]/50 flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-[#D4AF37] rounded-full animate-pulse"></div>
          </div>
        </div>
      </div>

      {/* Decorative Bottom Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white/60 via-white/20 to-transparent z-[5]"></div>

      {/* Subtle Zoom Animation - Very slow and gentle */}
      <style jsx>{`
        @keyframes subtleZoom {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }
      `}</style>
    </section>
  );
};

export default HeroLuxury;
